import { t } from "@lingui/macro";
import { functions } from "@repo/ui";
import { httpsCallable } from "firebase/functions";
import { ShieldCheck } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CollapsibleSettingsPanel } from "./CollapsibleSettingsPanel";
import { Button } from "./ui/button";

interface PendingOperation {
	requestId: string;
	operation:
		| "service_tags.delete"
		| "company_users.remove"
		| "playbook_runs.cancel"
		| "phone_agents.rollback_live"
		| "services.delete"
		| "phone_agents.delete";
	targetId: string;
	parameters: { serviceId?: string; id: string; targetVersion?: number };
	credentialId: string;
	clientId: string | null;
	createdAt: string;
	expiresAt: string;
}

export function ExternalOperationApprovalsCard({
	companyId,
}: {
	companyId: string;
}) {
	const [requests, setRequests] = useState<PendingOperation[]>([]);
	const [loading, setLoading] = useState(false);
	const [deciding, setDeciding] = useState<string | null>(null);
	const load = useCallback(async () => {
		setLoading(true);
		try {
			const call = httpsCallable<
				{ companyId: string },
				{ requests: PendingOperation[] }
			>(functions, "listExternalApprovalRequests");
			setRequests((await call({ companyId })).data.requests);
		} catch {
			toast.error(
				t({
					id: "operationApprovalsLoadFailed",
					message: "Unable to load operation approvals.",
				}),
			);
		} finally {
			setLoading(false);
		}
	}, [companyId]);
	useEffect(() => {
		void load();
	}, [load]);
	const decide = async (
		item: PendingOperation,
		decision: "approve" | "deny",
	) => {
		setDeciding(item.requestId);
		try {
			const call = httpsCallable(functions, "decideExternalApprovalRequest");
			await call({ companyId, requestId: item.requestId, decision });
			setRequests((current) =>
				current.filter((request) => request.requestId !== item.requestId),
			);
			toast.success(
				decision === "approve"
					? t({
							id: "operationApprovalQueued",
							message: "Operation approved and queued.",
						})
					: t({ id: "operationApprovalDenied", message: "Operation denied." }),
			);
		} catch (caught) {
			toast.error(
				caught instanceof Error
					? caught.message
					: t({
							id: "operationApprovalFailed",
							message: "Unable to decide this request.",
						}),
			);
		} finally {
			setDeciding(null);
		}
	};
	return (
		<CollapsibleSettingsPanel
			icon={<ShieldCheck className="h-5 w-5" />}
			title={t({
				id: "operationApprovalsTitle",
				message: "API operation approvals",
			})}
			description={t({
				id: "operationApprovalsDescription",
				message:
					"Review operations that require approval through the Developer API or MCP.",
			})}
		>
			<div className="space-y-3">
				<Button
					variant="outline"
					onClick={() => void load()}
					disabled={loading}
				>
					{t({ id: "operationApprovalsRefresh", message: "Refresh requests" })}
				</Button>
				{!loading && requests.length === 0 && (
					<p className="text-sm text-muted-foreground">
						{t({
							id: "operationApprovalsEmpty",
							message: "No operations are awaiting approval.",
						})}
					</p>
				)}
				{requests.map((item) => (
					<div key={item.requestId} className="space-y-2 rounded-md border p-4">
						<p className="font-medium">
							{item.operation === "service_tags.delete"
								? t({
										id: "operationApprovalDeleteTag",
										message: "Permanently delete service tag",
									})
								: item.operation === "company_users.remove"
									? t({
											id: "operationApprovalRemoveUser",
											message: "Remove company user",
										})
									: item.operation === "playbook_runs.cancel"
										? t({
												id: "operationApprovalCancelRun",
												message: "Cancel outbound playbook run",
											})
										: item.operation === "phone_agents.rollback_live"
											? t({
													id: "operationApprovalRollbackLive",
													message: "Roll back phone agent Live version",
												})
											: item.operation === "services.delete"
												? t({
														id: "operationApprovalDeleteService",
														message: "Delete service",
													})
												: t({
														id: "operationApprovalDeleteAgent",
														message: "Delete phone agent",
													})}
						</p>
						<p className="text-sm">
							<bdi>{item.targetId}</bdi>
							{item.parameters.targetVersion !== undefined && (
								<>
									{" "}
									·{" "}
									{t({
										id: "operationApprovalTargetVersion",
										message: "Target version",
									})}
									: {item.parameters.targetVersion}
								</>
							)}
							{item.parameters.serviceId && (
								<>
									{" "}
									· {t({ id: "operationApprovalService", message: "Service" })}:{" "}
									<bdi>{item.parameters.serviceId}</bdi>
								</>
							)}
						</p>
						<p className="text-sm text-muted-foreground">
							{t({
								id: "operationApprovalRequestedBy",
								message: "Requested by API key",
							})}
							: <bdi>{item.credentialId}</bdi>
							{item.clientId ? ` (${item.clientId})` : ""}
						</p>
						<p className="text-sm text-muted-foreground">
							{t({ id: "operationApprovalRequestId", message: "Request ID" })}:{" "}
							<bdi>{item.requestId}</bdi>
						</p>
						<p className="text-sm text-muted-foreground">
							{t({ id: "operationApprovalExpires", message: "Expires" })}:{" "}
							<bdi>{new Date(item.expiresAt).toLocaleString()}</bdi>
						</p>
						<div className="flex gap-2">
							<Button
								disabled={
									deciding !== null || Date.parse(item.expiresAt) <= Date.now()
								}
								onClick={() => void decide(item, "approve")}
							>
								{t({
									id: "operationApprovalApprove",
									message: "Approve operation",
								})}
							</Button>
							<Button
								variant="outline"
								disabled={deciding !== null}
								onClick={() => void decide(item, "deny")}
							>
								{t({ id: "operationApprovalDeny", message: "Deny" })}
							</Button>
						</div>
					</div>
				))}
			</div>
		</CollapsibleSettingsPanel>
	);
}
